/**
 * A manifest for the preview sandbox: rotation, fit and the closed and idle
 * states, demonstrable with no paired screen and no media in the library.
 *
 * The preview also runs against real documents now — a screen's own manifest, or
 * a playlist being edited — and that is what the playlists page shows. This stays
 * because the two answer different questions: those show what a particular screen
 * will do, this shows what the three screen states look like at all, which is
 * exactly what somebody deciding how to hang a TV needs before anything exists.
 *
 * Nothing here pretends to be live data: the artwork is drawn inline as SVG and
 * the shapes go through `deviceManifestSchema` exactly as the API's output will,
 * so the sandbox exercises the real resolver rather than a mock of it. The three
 * scenarios differ only in what the manifest says — the closed state comes from
 * opening hours that do not contain right now, and the idle state from a screen
 * with nothing assigned — so `resolve()` is what decides, not a switch in the UI.
 */

import { deviceManifestSchema, zonedParts } from '@pbtv/shared';
import type { DeviceManifest, OpeningHoursEntry, RotationDegrees } from '@pbtv/shared';

export type PreviewScenario = 'playing' | 'closed' | 'idle';

const SAMPLE_TIMEZONE = 'Pacific/Auckland';
const SAMPLE_SHA256 = 'b'.repeat(64);

interface CardOptions {
  width: number;
  height: number;
  background: string;
  accent: string;
  kicker: string;
  headline: string;
  footer: string;
}

/**
 * Artwork as a data URL. `manifestAssetSchema` only asks for a parseable URL, so
 * a data URL satisfies the contract and keeps the sandbox working with no CDN,
 * no network, and no binary checked into the repo.
 */
function promoCard(options: CardOptions): string {
  const { width, height, background, accent, kicker, headline, footer } = options;
  const pad = Math.round(width * 0.07);
  const svg = [
    `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">`,
    `<rect width="${width}" height="${height}" fill="${background}"/>`,
    `<rect x="0" y="0" width="${width}" height="${Math.round(height * 0.018)}" fill="${accent}"/>`,
    `<g font-family="Helvetica, Arial, sans-serif" fill="#ffffff">`,
    `<text x="${pad}" y="${Math.round(height * 0.36)}" font-size="${Math.round(height * 0.06)}" letter-spacing="${Math.round(height * 0.014)}" fill="${accent}">${kicker}</text>`,
    `<text x="${pad}" y="${Math.round(height * 0.58)}" font-size="${Math.round(height * 0.16)}" font-weight="bold">${headline}</text>`,
    `<text x="${pad}" y="${Math.round(height * 0.74)}" font-size="${Math.round(height * 0.055)}" opacity="0.72">${footer}</text>`,
    `</g>`,
    `</svg>`,
  ].join('');
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

function sampleAsset(
  id: string,
  width: number,
  height: number,
  card: Omit<CardOptions, 'width' | 'height'>,
): Record<string, unknown> {
  return {
    id,
    // v1 does not accept SVG uploads, so the sample declares the type a real
    // still would have; only video-versus-still matters to the resolver.
    type: 'image/png',
    url: promoCard({ width, height, ...card }),
    sha256: SAMPLE_SHA256,
    bytes: 512 * 1024,
    width,
    height,
    durationMs: null,
    contentExpiresAt: null,
  };
}

const SAMPLE_ASSETS: Record<string, unknown> = {
  promo_two_for_tuesday: sampleAsset('promo_two_for_tuesday', 1920, 1080, {
    background: '#12100f',
    accent: '#e2563c',
    kicker: 'TUESDAY ONLY',
    headline: 'Two for one',
    footer: 'Any two classic pizzas, dine in or takeaway',
  }),
  promo_garlic_bread: sampleAsset('promo_garlic_bread', 1080, 1350, {
    background: '#2a1108',
    accent: '#f0b429',
    kicker: 'ADD ON',
    headline: 'Garlic bread',
    footer: 'Fresh from the deck oven',
  }),
  promo_new_store: sampleAsset('promo_new_store', 2560, 900, {
    background: '#0b1c1a',
    accent: '#4fd1a5',
    kicker: 'NOW OPEN',
    headline: 'Te Puke',
    footer: 'Corner of Jellicoe and Oxford',
  }),
};

/** Minutes since midnight, formatted the way the manifest wants them. */
function formatMinuteOfDay(minuteOfDay: number): string {
  const wrapped = ((minuteOfDay % 1440) + 1440) % 1440;
  const hours = Math.floor(wrapped / 60);
  const minutes = wrapped % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

/**
 * Opening hours that certainly do not contain right now: a one-hour window
 * starting two hours from now, every day. The closed state then comes out of the
 * resolver honestly instead of being asserted by the sandbox.
 */
function hoursStartingLater(nowMs: number): OpeningHoursEntry[] {
  const now = zonedParts(nowMs, SAMPLE_TIMEZONE);
  const open = now.minuteOfDay + 120;
  const entry = { open: formatMinuteOfDay(open), close: formatMinuteOfDay(open + 60) };
  return ([1, 2, 3, 4, 5, 6, 7] as const).map((day) => ({ day, ...entry }));
}

export function sampleManifest(
  scenario: PreviewScenario,
  rotationDegrees: RotationDegrees,
  nowMs: number = Date.now(),
): DeviceManifest {
  const base = {
    manifestVersion: `sha256:${'0'.repeat(64)}`,
    generatedAt: new Date(nowMs).toISOString(),
    screen: { id: 'scr_sandbox', name: 'Counter screen', rotationDegrees },
    timezone: SAMPLE_TIMEZONE,
    polling: {},
  };

  if (scenario === 'idle') {
    return deviceManifestSchema.parse(base);
  }

  return deviceManifestSchema.parse({
    ...base,
    openingHours: scenario === 'closed' ? hoursStartingLater(nowMs) : [],
    defaultPlaylistId: 'pl_sandbox',
    playlists: {
      pl_sandbox: {
        id: 'pl_sandbox',
        name: 'Sandbox loop',
        items: [
          {
            assetId: 'promo_two_for_tuesday',
            durationSec: 6,
            fit: 'contain',
            backgroundColor: '#000000',
          },
          {
            assetId: 'promo_garlic_bread',
            durationSec: 5,
            fit: 'contain',
            backgroundColor: '#2a1108',
          },
          {
            assetId: 'promo_new_store',
            durationSec: 4,
            fit: 'cover',
            backgroundColor: '#000000',
          },
        ],
      },
    },
    assets: SAMPLE_ASSETS,
    referencedBytes: 3 * 512 * 1024,
  });
}
